import { IDockviewPanelProps } from "dockview-react";
import React, { useEffect, useRef, useState } from "react";
import { api } from "./utils/api";
import { underlyingLayout } from "./ibkr/layouts";
import { ModelParams, SolveFor } from "./OptionSolver";
import Plotter from "./utils/Plotter";

interface HistBar {
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
  x: string; // "YYYY-MM-DD HH:MM:SS"
}

interface SimBar {
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
  minute_candle_time: string; // "YYYY-MM-DD HH:MM"
  show_time: string; // "YYYY-MM-DD HH:MM:SS"
}

interface SimChartData {
  historical_bars: HistBar[];
  simulation_bars: SimBar[];
}

interface OptionPosition {
  side: "BUY" | "SELL";
  type: "CALL" | "PUT";
  strike: number;
  underlying: number;
  expiry_in_days: number;
  iv: number;
  price: number;
  size: number;
  total: number;
  timestamp: string; // "YYYY-MM-DD HH:MM:SS"
}

function addSecs(t: string, n: number): string {
  const d = new Date(t.replace(" ", "T") + "Z");
  d.setUTCSeconds(d.getUTCSeconds() + n);
  return d.toISOString().slice(0, 19).replace("T", " ");
}

function padTime(t: string): string {
  return t.length === 16 ? t + ":00" : t;
}

export const SimulatedChart = (props: IDockviewPanelProps) => {
  const dataRef = useRef<SimChartData | null>(null);
  const clockRef = useRef<string>("");
  const endRef = useRef<string>("");
  const lastSimBarIndexRef = useRef<number>(0);

  // ── form ────────────────────────────────────────────────────────────────────
  const [requestData, setRequestData] = useState({
    ticker: "AAPL",
    absolute_start: "2026-05-11 00:00",
    simulation_start: "2026-05-12 09:30",
    absolute_end: "2026-05-12 10:30",
  });

  // ── ui state ─────────────────────────────────────────────────────────────────
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [hasFetched, setHasFetched] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [clockDisplay, setClockDisplay] = useState("--:--:--");
  const [jumpInput, setJumpInput] = useState("");
  const [underlyingData, setUnderlyingData] = useState<any[]>([]);
  const [lastClose, setLastClose] = useState<number | null>(null);
  const [compress, setCompress] = useState(false);

  // ── shortcut / focus tracking ─────────────────────────────────────────────
  const [isFocusingTopForm, setIsFocusingTopForm] = useState(false);

  // ── chart click interceptor ───────────────────────────────────────────────
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const priceScanInputRef = useRef<HTMLInputElement>(null);
  const [priceScanInput, setPriceScanInput] = useState("");
  const [priceScanResult, setPriceScanResult] = useState<number | null>(null);
  const [isPriceScanFocused, setIsPriceScanFocused] = useState(false);

  // ── IV editing per-row ────────────────────────────────────────────────────
  const [editingPositionIv, setEditingPositionIv] = useState<Record<number, string>>({});

  // ── restore form from localStorage on mount ──────────────────────────────────
  useEffect(() => {
    const stored = localStorage.getItem("simulatedChartForm");
    if (stored) {
      try {
        const { ticker: t, absolute_start: a, simulation_start: s, absolute_end: e } = JSON.parse(stored);
        if (t && a && s && e) setRequestData({ ticker: t, absolute_start: a, simulation_start: s, absolute_end: e });
      } catch {
        // ignore
      }
    }
  }, []);

  // ── keyboard shortcuts (C/P/B/S) ─────────────────────────────────────────
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // Only active when this panel is the active dockview panel
      if (props.containerApi?.activePanel?.id !== props.api.id) return;
      // Ignore when user is editing the top form inputs
      if (isFocusingTopForm) return;

      const key = e.key.toLowerCase();
      if (!["c", "p", "b", "s"].includes(key)) return;

      e.preventDefault();
      e.stopPropagation();
      (e as any).stopImmediatePropagation?.();

      switch (key) {
        case "c": setNewOptionPositionInputData((d) => ({ ...d, type: "CALL" })); break;
        case "p": setNewOptionPositionInputData((d) => ({ ...d, type: "PUT" })); break;
        case "b": setNewOptionPositionInputData((d) => ({ ...d, side: "BUY" })); break;
        case "s": setNewOptionPositionInputData((d) => ({ ...d, side: "SELL" })); break;
      }
    };

    window.addEventListener("keydown", handler, { capture: true });
    return () => window.removeEventListener("keydown", handler, { capture: true } as any);
  }, [props.api, props.containerApi, isFocusingTopForm]);

  // ── clock effect ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isPlaying) return;
    if (dataRef.current?.simulation_bars.length === lastSimBarIndexRef.current + 1) {
      setIsPlaying(false);
      return;
    }

    const id = setInterval(() => {
      const next = addSecs(clockRef.current, 1);
      if (next > endRef.current) { setIsPlaying(false); return; }
      clockRef.current = next;
      setClockDisplay(next.slice(11));

      if (!dataRef.current) return;

      if (
        lastSimBarIndexRef.current + 1 < dataRef.current.simulation_bars.length &&
        dataRef.current.simulation_bars[lastSimBarIndexRef.current + 1].show_time <= next
      ) {
        const currentBar = dataRef.current.simulation_bars[lastSimBarIndexRef.current];
        const nextBar = dataRef.current.simulation_bars[lastSimBarIndexRef.current + 1];
        const currentMinute = currentBar.minute_candle_time;
        const nextMinute = nextBar.minute_candle_time;

        if (nextMinute !== currentMinute) {
          const newDate = new Date(nextBar.minute_candle_time);
          setUnderlyingData((prev) => {
            const t = prev[0] as any;
            return [{ ...t, x: [...t.x, newDate], open: [...t.open, nextBar.open], high: [...t.high, nextBar.high], low: [...t.low, nextBar.low], close: [...t.close, nextBar.close], customdata: [...t.customdata, [nextBar.open, nextBar.high, nextBar.low, nextBar.close, nextBar.volume]] }];
          });
        } else {
          setUnderlyingData((prev) => {
            const t = prev[0] as any;
            return [{ ...t, x: [...t.x.slice(0, -1), new Date(nextBar.minute_candle_time)], open: [...t.open.slice(0, -1), nextBar.open], high: [...t.high.slice(0, -1), nextBar.high], low: [...t.low.slice(0, -1), nextBar.low], close: [...t.close.slice(0, -1), nextBar.close], customdata: [...t.customdata.slice(0, -1), [nextBar.open, nextBar.high, nextBar.low, nextBar.close, nextBar.volume]] }];
          });
        }

        setLastClose(nextBar.close);
        lastSimBarIndexRef.current += 1;
      }
    }, 1000);

    return () => clearInterval(id);
  }, [isPlaying]);

  // ── fetch ────────────────────────────────────────────────────────────────────
  const fetchData = async () => {
    localStorage.setItem("simulatedChartForm", JSON.stringify(requestData));
    setIsLoading(true);
    setIsPlaying(false);
    setErrorMsg("");
    setHasFetched(false);
    setLastClose(null);
    dataRef.current = null;
    lastSimBarIndexRef.current = 0;

    try {
      const resp = await api.get<SimChartData>("/simulate/chart", { params: requestData });
      dataRef.current = resp.data;
      const startFull = padTime(requestData.simulation_start);
      clockRef.current = startFull;
      endRef.current = padTime(requestData.absolute_end);
      setClockDisplay(startFull.slice(11));
      setJumpInput(startFull.slice(11));
      setHasFetched(true);

      const underlyingCandlesticks = {
        x: resp.data.historical_bars.map((d) => new Date(d.x)),
        close: resp.data.historical_bars.map((d) => d.close),
        decreasing: { line: { color: "#7F7F7F" } },
        high: resp.data.historical_bars.map((d) => d.high),
        increasing: { line: { color: "#17BECF" } },
        low: resp.data.historical_bars.map((d) => d.low),
        open: resp.data.historical_bars.map((d) => d.open),
        customdata: resp.data.historical_bars.map((d) => [d.open, d.high, d.low, d.close, d.volume]),
        hovertemplate: "O: %{customdata[0]:.2f}  H: %{customdata[1]:.2f}  L: %{customdata[2]:.2f}  C: <b>%{customdata[3]:.2f}</b><br>Vol: <b>%{customdata[4]}</b><br><extra></extra>",
        line: { color: "rgba(31,119,180,1)" },
        type: "candlestick",
        xaxis: "x",
        yaxis: "y",
      };

      if (resp.data.simulation_bars.length > 0) {
        const fb = resp.data.simulation_bars[0];
        underlyingCandlesticks.x.push(new Date(fb.minute_candle_time));
        underlyingCandlesticks.open.push(fb.open);
        underlyingCandlesticks.high.push(fb.high);
        underlyingCandlesticks.low.push(fb.low);
        underlyingCandlesticks.close.push(fb.close);
        underlyingCandlesticks.customdata.push([fb.open, fb.high, fb.low, fb.close, fb.volume]);
      }

      setUnderlyingData([underlyingCandlesticks]);
    } catch (err: any) {
      setErrorMsg(err?.response?.data?.detail || err?.message || "Request failed");
    } finally {
      setIsLoading(false);
    }
  };

  const jumpToTime = () => {
    setIsPlaying(false);
    if (!dataRef.current) return;

    const raw = jumpInput.trim();
    const target = /^\d{2}:\d{2}:\d{2}$/.test(raw)
      ? `${clockRef.current.slice(0, 10)} ${raw}`
      : raw;

    const bars = dataRef.current.simulation_bars;
    if (!bars.length) return;

    const first = bars[0].show_time;
    const last = bars[bars.length - 1].show_time;
    const clamped = target < first ? first : target > last ? last : target;

    let newIndex = 0;
    for (let i = 0; i < bars.length; i++) {
      if (bars[i].show_time <= clamped) newIndex = i;
      else break;
    }

    const minuteMap = new Map<string, SimBar>();
    for (let i = 0; i <= newIndex; i++) minuteMap.set(bars[i].minute_candle_time, bars[i]);

    const hist = dataRef.current.historical_bars;
    const simBars = Array.from(minuteMap.values());

    setUnderlyingData((prev) => {
      const base = prev[0] as any;
      return [{
        ...base,
        x: [...hist.map((d) => new Date(d.x)), ...simBars.map((b) => new Date(b.minute_candle_time))],
        open: [...hist.map((d) => d.open), ...simBars.map((b) => b.open)],
        high: [...hist.map((d) => d.high), ...simBars.map((b) => b.high)],
        low: [...hist.map((d) => d.low), ...simBars.map((b) => b.low)],
        close: [...hist.map((d) => d.close), ...simBars.map((b) => b.close)],
        customdata: [...hist.map((d) => [d.open, d.high, d.low, d.close, d.volume]), ...simBars.map((b) => [b.open, b.high, b.low, b.close, b.volume])],
      }];
    });

    clockRef.current = clamped;
    setClockDisplay(clamped.slice(11));
    lastSimBarIndexRef.current = newIndex;
    setLastClose(bars[newIndex].close);
  };

  // ── option simulator ──────────────────────────────────────────────────────
  const [filledOptionPositions, setFilledOptionPositions] = useState<OptionPosition[]>([]);
  const [fillDisabled, setFillDisabled] = useState(true);

  const [newOptionPositionInputData, setNewOptionPositionInputData] = useState({
    side: "BUY",
    type: "CALL",
    strike: "",
    underlying: 0,
    expiry_in_days: "",
    iv: "",
    price: 0,
    size: "",
    total: 0,
  });

  useEffect(() => {
    const size = Number(newOptionPositionInputData.size);
    const price = Number(newOptionPositionInputData.price);
    setFillDisabled(
      !newOptionPositionInputData.strike ||
        !newOptionPositionInputData.expiry_in_days ||
        !newOptionPositionInputData.iv ||
        isNaN(size) || size <= 0 ||
        isNaN(price) || price <= 0 ||
        !lastClose,
    );
  }, [newOptionPositionInputData, lastClose]);

  function calculateUnsoldSize(): number {
    const bought = filledOptionPositions.filter((p) => p.side === "BUY").reduce((s, p) => s + p.size, 0);
    const sold = filledOptionPositions.filter((p) => p.side === "SELL").reduce((s, p) => s + p.size, 0);
    return bought - sold;
  }

  function calculateTotal(): string {
    const size = Number(newOptionPositionInputData.size);
    const price = Number(newOptionPositionInputData.price);
    if (isNaN(size) || isNaN(price)) return "N/A";
    return (size * price * 100).toFixed(2);
  }

  const calculateOptionPrice = async (): Promise<void> => {
    const strike = Number(newOptionPositionInputData.strike);
    const expiry = Number(newOptionPositionInputData.expiry_in_days);
    const iv = Number(newOptionPositionInputData.iv);
    const underlying = lastClose ?? 0;
    if (isNaN(strike) || strike <= 0 || isNaN(expiry) || expiry <= 0 || isNaN(iv) || iv <= 0 || underlying <= 0) return;

    try {
      const response = await api.post("/option-solver", {
        u: underlying, vol: iv, s: strike, r: 0.04, t: expiry,
        type: newOptionPositionInputData.type === "CALL" ? "call" : "put",
        price: 0, solveFor: SolveFor.price, timeUnits: "d",
      } as ModelParams);
      const price: number = response.data.price;
      setNewOptionPositionInputData((d) => ({ ...d, underlying, price, total: price * Number(d.size) * 100 }));
    } catch (err: any) {
      console.error("Failed to calculate option price:", err);
    }
  };

  useEffect(() => {
    calculateOptionPrice();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newOptionPositionInputData.strike, newOptionPositionInputData.expiry_in_days, newOptionPositionInputData.iv, newOptionPositionInputData.type, lastClose]);

  async function handleFillingOptionPosition(e: React.FormEvent) {
    e.preventDefault();
    if (fillDisabled) return;
    const requestedSize = Number(newOptionPositionInputData.size);
    if (newOptionPositionInputData.side === "SELL" && requestedSize > calculateUnsoldSize()) return;

    const localLastClose = dataRef.current!.simulation_bars[lastSimBarIndexRef.current].close;
    let localPrice: number;
    try {
      const response = await api.post("/option-solver", {
        u: localLastClose, vol: Number(newOptionPositionInputData.iv),
        s: Number(newOptionPositionInputData.strike), r: 0.04,
        t: Number(newOptionPositionInputData.expiry_in_days),
        type: newOptionPositionInputData.type === "CALL" ? "call" : "put",
        price: 0, solveFor: SolveFor.price, timeUnits: "d",
      } as ModelParams);
      localPrice = response.data.price;
    } catch {
      throw new Error("Failed to recalculate price for filling the position");
    }

    const newPosition: OptionPosition = {
      side: newOptionPositionInputData.side as "BUY" | "SELL",
      type: newOptionPositionInputData.type as "CALL" | "PUT",
      strike: Number(newOptionPositionInputData.strike),
      underlying: localLastClose,
      expiry_in_days: Number(newOptionPositionInputData.expiry_in_days),
      iv: Number(newOptionPositionInputData.iv),
      price: localPrice,
      size: Number(newOptionPositionInputData.size),
      total: localPrice * Number(newOptionPositionInputData.size) * 100,
      timestamp: clockRef.current,
    };

    setFilledOptionPositions((prev) => [...prev, newPosition]);
  }

  // ── position IV editing ───────────────────────────────────────────────────
  function handlePositionIvChange(index: number, ivStr: string) {
    const iv = parseFloat(ivStr);
    if (isNaN(iv) || iv <= 0) return;
    const pos = filledOptionPositions[index];
    if (!pos) return;

    setFilledOptionPositions((prev) => prev.map((p, i) => (i === index ? { ...p, iv } : p)));

    api.post("/option-solver", {
      u: pos.underlying, vol: iv, s: pos.strike, r: 0.04,
      t: pos.expiry_in_days,
      type: pos.type === "CALL" ? "call" : "put",
      price: 0, solveFor: SolveFor.price, timeUnits: "d",
    } as ModelParams).then((resp) => {
      const newPrice: number = resp.data.price;
      setFilledOptionPositions((prev) =>
        prev.map((p, i) => (i === index ? { ...p, iv, price: newPrice, total: newPrice * p.size * 100 } : p))
      );
    }).catch(() => {});
  }

  function handleDeletePosition(index: number) {
    if (!window.confirm("Delete this position?")) return;
    setFilledOptionPositions((prev) => prev.filter((_, i) => i !== index));
    setEditingPositionIv((prev) => {
      const next: Record<number, string> = {};
      Object.entries(prev).forEach(([k, v]) => {
        const i = parseInt(k);
        if (i < index) next[i] = v;
        else if (i > index) next[i - 1] = v;
      });
      return next;
    });
  }

  // ── price scan calculation ────────────────────────────────────────────────
  useEffect(() => {
    const underlying = parseFloat(priceScanInput);
    const strike = parseFloat(newOptionPositionInputData.strike as string);
    const expiry = parseFloat(newOptionPositionInputData.expiry_in_days as string);
    const iv = parseFloat(newOptionPositionInputData.iv as string);

    if (isNaN(underlying) || underlying <= 0 || isNaN(strike) || strike <= 0 || isNaN(expiry) || expiry <= 0 || isNaN(iv) || iv <= 0) {
      setPriceScanResult(null);
      return;
    }

    let cancelled = false;
    api.post("/option-solver", {
      u: underlying, vol: iv, s: strike, r: 0.04, t: expiry,
      type: newOptionPositionInputData.type === "CALL" ? "call" : "put",
      price: 0, solveFor: SolveFor.price, timeUnits: "d",
    } as ModelParams).then((resp) => {
      if (!cancelled) setPriceScanResult(resp.data.price);
    }).catch(() => {
      if (!cancelled) setPriceScanResult(null);
    });

    return () => { cancelled = true; };
  }, [priceScanInput, newOptionPositionInputData.strike, newOptionPositionInputData.expiry_in_days, newOptionPositionInputData.iv, newOptionPositionInputData.type]);

  // ── chart overlay click handler ───────────────────────────────────────────
  function handleChartOverlayMouseDown(e: React.MouseEvent<HTMLDivElement>) {
    e.preventDefault();
    const plotEl = chartContainerRef.current?.querySelector(".js-plotly-plot") as any;
    if (!plotEl?._fullLayout) return;
    const yaxis = plotEl._fullLayout.yaxis;
    if (!yaxis?.range || !yaxis._length) return;
    const bb = plotEl.getBoundingClientRect();
    const relY = e.clientY - bb.top;
    if (relY < yaxis._offset || relY > yaxis._offset + yaxis._length) return;
    const dataY = yaxis.range[1] - ((relY - yaxis._offset) / yaxis._length) * (yaxis.range[1] - yaxis.range[0]);
    setPriceScanInput(Math.max(0, dataY).toFixed(2));
    priceScanInputRef.current?.focus();
  }

  // ── position markers ──────────────────────────────────────────────────────
  function buildPositionMarkers(positions: OptionPosition[]) {
    const mk = (symbol: string, color: string) => ({ size: 12, color, symbol, line: { color: "black", width: 1 } });
    const buys = positions.filter((p) => p.side === "BUY");
    const sells = positions.filter((p) => p.side === "SELL");
    const traces = [];
    const toX = (ts: string) => new Date(ts.replace(" ", "T"));

    if (buys.length > 0) {
      traces.push({
        type: "scatter", mode: "markers", name: "Buy",
        x: buys.map((p) => toX(p.timestamp)), y: buys.map((p) => p.underlying),
        marker: mk("triangle-up", "yellow"),
        customdata: buys.map((p) => [p.type, p.strike, p.price, p.size, (p.price * p.size * 100).toFixed(2)]),
        hovertemplate: "BUY %{customdata[0]} %{customdata[1]}<br>Price: %{customdata[2]}<br>Size: %{customdata[3]}<br>Total: %{customdata[4]}<extra></extra>",
        showlegend: true,
      });
    }

    if (sells.length > 0) {
      traces.push({
        type: "scatter", mode: "markers", name: "Sell",
        x: sells.map((p) => toX(p.timestamp)), y: sells.map((p) => p.underlying),
        marker: mk("square", "orange"),
        customdata: sells.map((p) => [p.type, p.strike, p.price, p.size, (p.price * p.size * 100).toFixed(2)]),
        hovertemplate: "SELL %{customdata[0]} %{customdata[1]}<br>Price: %{customdata[2]}<br>Size: %{customdata[3]}<br>Total: %{customdata[4]}<extra></extra>",
        showlegend: true,
      });
    }

    return traces;
  }

  // ── positions table ───────────────────────────────────────────────────────
  function renderFilledPositions() {
    const headers = ["Side", "Type", "Strike", "Underlying", "Expiry", "IV", "Price", "Size", "Total", ""];

    const buys = filledOptionPositions.filter((p) => p.side === "BUY");
    const sells = filledOptionPositions.filter((p) => p.side === "SELL");
    const avgBuyPrice = buys.length ? buys.reduce((s, p) => s + p.price, 0) / buys.length : null;
    const avgSellPrice = sells.length ? sells.reduce((s, p) => s + p.price, 0) / sells.length : null;
    const netTotal = filledOptionPositions.reduce((s, p) => s + (p.side === "BUY" ? -p.total : p.total), 0);
    const totalContracts = filledOptionPositions.reduce((s, p) => s + p.size, 0);
    const totalBuyContracts = buys.reduce((s, p) => s + p.size, 0);
    const totalSellContracts = sells.reduce((s, p) => s + p.size, 0);
    const isFlat = totalBuyContracts > 0 && totalBuyContracts === totalSellContracts;
    const totalPaid = buys.reduce((s, p) => s + p.total, 0);
    const totalGain = sells.reduce((s, p) => s + p.total, 0);
    const pctGain = isFlat && totalPaid > 0 ? ((totalGain - totalPaid) / totalPaid) * 100 : null;

    return (
      <div className="rounded-lg overflow-y-auto grow">
        <div className="grid grid-cols-10 text-xs font-semibold uppercase tracking-wider">
          {headers.map((h, i) => (
            <div key={i} className="px-3 py-2 text-center">{h}</div>
          ))}
        </div>

        {filledOptionPositions.map((pos, index) => {
          const signedTotal = pos.side === "BUY" ? -pos.total : pos.total;
          const ivVal = editingPositionIv[index] !== undefined ? editingPositionIv[index] : String(pos.iv);
          return (
            <div key={index} className={`grid grid-cols-10 text-sm ${index % 2 === 0 ? "" : "bg-gray-100"}`}>
              <div className="px-3 py-2 text-center font-bold">{pos.side}</div>
              <div className="px-3 py-2 text-center">{pos.type}</div>
              <div className="px-3 py-2 text-center">{pos.strike}</div>
              <div className="px-3 py-2 text-center">{pos.underlying}</div>
              <div className="px-3 py-2 text-center">{pos.expiry_in_days}d</div>
              <div className="px-3 py-2 text-center">
                <input
                  type="number"
                  step="0.01"
                  className="w-16 text-center bg-transparent border-b border-gray-300 focus:border-gray-600 outline-none"
                  value={ivVal}
                  onChange={(e) =>
                    setEditingPositionIv((prev) => ({ ...prev, [index]: e.target.value }))
                  }
                  onBlur={(e) => {
                    handlePositionIvChange(index, e.target.value);
                    setEditingPositionIv((prev) => {
                      const next = { ...prev };
                      delete next[index];
                      return next;
                    });
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handlePositionIvChange(index, (e.target as HTMLInputElement).value);
                      (e.target as HTMLInputElement).blur();
                    }
                  }}
                />
              </div>
              <div className="px-3 py-2 text-center">{pos.price.toFixed(4)}</div>
              <div className="px-3 py-2 text-center">{pos.size}</div>
              <div className="px-3 py-2 text-center font-bold">
                {signedTotal < 0 ? "-" : "+"}
                {Math.abs(signedTotal).toFixed(2)}
              </div>
              <div className="px-3 py-2 text-center">
                <button
                  type="button"
                  onClick={() => handleDeletePosition(index)}
                  className="text-gray-400 hover:text-gray-700 transition-colors text-xs leading-none"
                >
                  ✕
                </button>
              </div>
            </div>
          );
        })}

        {filledOptionPositions.length > 0 && (
          <div className="grid grid-cols-10 text-xs font-semibold border-t border-gray-300 mt-1 pt-1">
            <div className="px-3 py-2 text-center col-span-5">
              {avgBuyPrice !== null && (
                <span className="mr-2">Avg Buy: {avgBuyPrice.toFixed(2)}</span>
              )}
              {avgSellPrice !== null && (
                <span className="mr-2">Avg Sell: {avgSellPrice.toFixed(2)}</span>
              )}
              <span>Contracts: {totalContracts}</span>
            </div>
            <div className="px-3 py-2 text-center" />
            <div className="px-3 py-2 text-center font-bold col-span-3 whitespace-nowrap">
              {isFlat && (
                <span>
                  {netTotal < 0 ? "-" : "+"}
                  {Math.abs(netTotal).toFixed(2)}
                  {pctGain !== null && (
                    <span className="ml-1 text-xs">
                      ({pctGain >= 0 ? "+" : ""}{pctGain.toFixed(1)}%)
                    </span>
                  )}
                </span>
              )}
            </div>
            <div />
          </div>
        )}
      </div>
    );
  }

  // ── render ───────────────────────────────────────────────────────────────────
  return (
    <div className="w-full h-full min-w-[300px] min-h-[300px] flex flex-col overflow-hidden">

      {/* ── Top bar: form ── */}
      <div className="flex gap-4 pl-2 pr-2">
        <form
          className="flex flex-row m-2 gap-2 items-end"
          onSubmit={(e) => { e.preventDefault(); fetchData(); }}
        >
          <label htmlFor="sc-ticker">
            Ticker:
            <input
              placeholder="AAPL"
              type="text"
              autoComplete="off"
              id="sc-ticker"
              name="ticker"
              value={requestData.ticker}
              onFocus={() => setIsFocusingTopForm(true)}
              onBlur={() => setIsFocusingTopForm(false)}
              onChange={(e) => setRequestData({ ...requestData, ticker: e.target.value })}
            />
          </label>

          <label htmlFor="sc-absStart">
            Abs. start:
            <input
              placeholder="YYYY-MM-DD HH:MM"
              type="text"
              autoComplete="off"
              id="sc-absStart"
              name="absStart"
              value={requestData.absolute_start}
              onFocus={() => setIsFocusingTopForm(true)}
              onBlur={() => setIsFocusingTopForm(false)}
              onChange={(e) => setRequestData({ ...requestData, absolute_start: e.target.value })}
            />
          </label>

          <label htmlFor="sc-simStart">
            Sim. start:
            <input
              placeholder="YYYY-MM-DD HH:MM"
              type="text"
              autoComplete="off"
              id="sc-simStart"
              name="simStart"
              value={requestData.simulation_start}
              onFocus={() => setIsFocusingTopForm(true)}
              onBlur={() => setIsFocusingTopForm(false)}
              onChange={(e) => setRequestData({ ...requestData, simulation_start: e.target.value })}
            />
          </label>

          <label htmlFor="sc-absEnd">
            Abs. end:
            <input
              placeholder="YYYY-MM-DD HH:MM"
              type="text"
              autoComplete="off"
              id="sc-absEnd"
              name="absEnd"
              value={requestData.absolute_end}
              onFocus={() => setIsFocusingTopForm(true)}
              onBlur={() => setIsFocusingTopForm(false)}
              onChange={(e) => setRequestData({ ...requestData, absolute_end: e.target.value })}
            />
          </label>

          <button type="submit" className="hidden">Submit</button>
        </form>
      </div>

      {/* ── Clock bar ── */}
      {hasFetched && (
        <div className="flex items-center gap-5 pl-2 pr-2 pb-1">
          <button type="button" className="btn btn-default w-32" onClick={() => setIsPlaying((p) => !p)}>
            {isPlaying ? "⏸ Stop" : "▶ Start"}
          </button>

          <span className="font-mono font-semibold tracking-widest min-w-[90px]">
            {clockDisplay}
            <span className="text-gray-400 ml-2">NYC</span>
          </span>

          {lastClose !== null && (
            <span className="font-mono font-semibold">{lastClose.toFixed(2)}</span>
          )}

          <label className="flex items-center gap-1 cursor-pointer text-xs">
            <span className="text-gray-400">Compress:</span>
            <input type="checkbox" checked={compress} onChange={(e) => setCompress(e.target.checked)} />
          </label>

          <div>
            <span className="text-sm text-gray-500">Jump:</span>
            <input
              className="font-mono w-24 text-center"
              autoComplete="off"
              value={jumpInput}
              onChange={(e) => setJumpInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") jumpToTime(); }}
              placeholder="HH:MM:SS"
            />
            <button type="button" className="btn btn-default" onClick={jumpToTime}>Set</button>
          </div>
        </div>
      )}

      {/* ── Chart area ── */}
      <div ref={chartContainerRef} className="w-full max-h-[400px] overflow-hidden shrink-0 relative">
        {errorMsg && <div className="text-red-500 ml-2">{errorMsg}</div>}
        {isLoading && <div className="ml-2">Loading...</div>}
        {!errorMsg && !isLoading && (underlyingData[0] as any)?.x?.length === 0 && (
          <div className="ml-2">No data available</div>
        )}
        {!errorMsg && !isLoading && (underlyingData[0] as any)?.x?.length > 0 && (
          <Plotter
            data={[...underlyingData, ...buildPositionMarkers(filledOptionPositions)]}
            layout={underlyingLayout}
            isLoading={isLoading}
            compress={compress}
          />
        )}
        {isPriceScanFocused && (
          <div
            className="absolute inset-0 cursor-crosshair z-10"
            onMouseDown={handleChartOverlayMouseDown}
          />
        )}
      </div>

      {hasFetched && (
        <>
          <div className="grow overflow-y-auto min-h-0">
            {renderFilledPositions()}
          </div>

          {/* ── Position entry form ── */}
          <form
            onSubmit={handleFillingOptionPosition}
            className="flex gap-4 mr-5 ml-5 mt-5 shrink-0"
          >
            <div>
              <div className="w-44">
                <span>Side:</span>
                <div>
                  <label htmlFor="side-buy" className="flex gap-2">
                    <input
                      id="side-buy"
                      type="radio"
                      name="side"
                      value="BUY"
                      checked={newOptionPositionInputData.side === "BUY"}
                      onChange={(e) => setNewOptionPositionInputData((d) => ({ ...d, side: e.target.value as "BUY" | "SELL" }))}
                    />
                    BUY
                  </label>
                  <label htmlFor="side-sell" className="flex gap-2 flex-nowrap">
                    <input
                      id="side-sell"
                      type="radio"
                      name="side"
                      value="SELL"
                      checked={newOptionPositionInputData.side === "SELL"}
                      onChange={(e) => setNewOptionPositionInputData((d) => ({ ...d, side: e.target.value as "BUY" | "SELL" }))}
                    />
                    SELL ({calculateUnsoldSize()})
                  </label>
                </div>
              </div>
            </div>

            <div>
              <div>
                <span>Type:</span>
                <div>
                  <label htmlFor="type-call" className="flex gap-2">
                    <input
                      id="type-call"
                      type="radio"
                      name="type"
                      value="CALL"
                      checked={newOptionPositionInputData.type === "CALL"}
                      onChange={(e) => setNewOptionPositionInputData((d) => ({ ...d, type: e.target.value as "CALL" | "PUT" }))}
                    />
                    CALL
                  </label>
                  <label htmlFor="type-put" className="flex gap-2">
                    <input
                      id="type-put"
                      type="radio"
                      name="type"
                      value="PUT"
                      checked={newOptionPositionInputData.type === "PUT"}
                      onChange={(e) => setNewOptionPositionInputData((d) => ({ ...d, type: e.target.value as "CALL" | "PUT" }))}
                    />
                    PUT
                  </label>
                </div>
              </div>
            </div>

            <div className="w-36">
              <div>
                <label htmlFor="strike">Strike:</label>
                <input
                  id="strike"
                  type="number"
                  autoComplete="off"
                  step="0.5"
                  value={newOptionPositionInputData.strike}
                  onChange={(e) => setNewOptionPositionInputData((d) => ({ ...d, strike: e.target.value }))}
                />
              </div>
              <div>U: {lastClose || "0"}</div>
            </div>

            <div className="w-36">
              <div>
                <label htmlFor="expiry">Exp in:</label>
                <input
                  id="expiry"
                  type="number"
                  autoComplete="off"
                  step="1"
                  value={newOptionPositionInputData.expiry_in_days}
                  onChange={(e) => setNewOptionPositionInputData((d) => ({ ...d, expiry_in_days: e.target.value }))}
                />
              </div>
            </div>

            <div className="w-36">
              <div>
                <label htmlFor="iv">
                  IV:
                  <div>
                    <input
                      id="iv"
                      type="number"
                      autoComplete="off"
                      step="0.01"
                      value={newOptionPositionInputData.iv}
                      onChange={(e) => setNewOptionPositionInputData((d) => ({ ...d, iv: e.target.value }))}
                    />
                  </div>
                </label>
              </div>
              <div>Price: {newOptionPositionInputData.price || "0"}</div>
            </div>

            <div className="w-36">
              <div>
                <label htmlFor="size">Size:</label>
                <input
                  id="size"
                  type="number"
                  autoComplete="off"
                  step="1"
                  value={newOptionPositionInputData.size}
                  onChange={(e) => setNewOptionPositionInputData((d) => ({ ...d, size: e.target.value }))}
                />
              </div>
              <div>Total: {calculateTotal()}</div>
            </div>

            <div>
              <button
                type="submit"
                className={`btn mt-5 h-12 ${fillDisabled ? "btn-secondary" : "btn-default"}`}
                disabled={fillDisabled}
              >
                Fill
              </button>
            </div>
          </form>

          {/* ── Fast price calculator ── */}
          <div className="flex items-center gap-3 ml-5 mb-3 mt-2 shrink-0">
            <label htmlFor="price-scan" className="text-sm whitespace-nowrap">
              New price (underlying):
            </label>
            <input
              id="price-scan"
              ref={priceScanInputRef}
              type="text"
              style={{ width: "10ch" }}
              className={`font-mono ${isPriceScanFocused ? "border-blue-400 ring-1 ring-blue-300" : ""}`}
              value={priceScanInput}
              onChange={(e) => setPriceScanInput(e.target.value)}
              onFocus={() => setIsPriceScanFocused(true)}
              onBlur={() => setIsPriceScanFocused(false)}
              placeholder="e.g. 195.50"
              autoComplete="off"
            />
            {priceScanResult !== null && (
              <span className="font-mono text-sm">
                → <strong>{priceScanResult.toFixed(4)}</strong>
              </span>
            )}
            {isPriceScanFocused && (
              <span className="text-xs text-gray-400 italic">click chart to set</span>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default SimulatedChart;
